import { Box, Button, Card, FlexBox, Icon, Link, tokens, Typography } from '@vp/swan';
import React, { ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { getFormattedTime } from '../../utils/miaw/common';
import './MessageComponent.styles.scss';
import { CAContext } from '../../contexts/CAContext';
import MessageFeedback from '../MessageFeedback/MessageFeedback';
import { IMessageAttachment } from '../../interfaces/agent-action-interfaces';
import SignInComponent from './SignInComponent';
import { MiawChatContext } from '../../contexts/MiawChatContext';
import { IUrlData } from '../../interfaces/app-interfaces';

export interface IMessageComponentBaseProps {
  id: string;
  messageType: 'chatbot' | 'user' | 'agent';
  messageContent: string | ReactNode;
  isLastMessage?: boolean;
  footer?: { isVisible: boolean; timeStamp?: number; agentName?: string };
  showFeedback?: boolean;
  additionalComponents?: ReactNode;
  isMessageRead?: boolean;
  disableTypingEffect?: boolean;
  onTypingComplete?: () => void;
  urlMapping?: IUrlData[];
  attachments?: IMessageAttachment;
  formatType?: string;
}
const MessageComponentBase: React.FC<IMessageComponentBaseProps> = ({
  messageContent,
  id,
  messageType,
  footer,
  additionalComponents,
  showFeedback,
  isMessageRead = true,
  isLastMessage = false,
  disableTypingEffect = false,
  onTypingComplete,
  urlMapping,
  attachments,
  formatType,
}) => {
  const {
    chatState: { scrollToBottom, chatWindow },
  } = useContext(CAContext);
  const {
    state: { agentName },
  } = useContext(MiawChatContext);
  const formattedTime = footer?.timeStamp ? getFormattedTime(footer.timeStamp) : '';
  const formattedAgentName = footer?.agentName ? `${footer.agentName} · ` : '';

  const [formattedMessageContent, setFormattedMessageContent] = useState<string | ReactNode>('');
  const [isTypingCompleted, setIsTypingCompleted] = useState(false);
  const [bubbleWidth, setBubbleWidth] = useState(290);

  useEffect(() => {
    if (isTypingCompleted) {
      // Call the callback when typing is complete
      if (onTypingComplete) {
        onTypingComplete();
        scrollToBottom?.();
      }
    }
  }, [isTypingCompleted, onTypingComplete]);

  const typeMessageWithLinks = useCallback(
    (message: string, speed: number) => {
      // Use linkify to process the message and get React elements for URLs
      const linkedParts = linkify(message);

      // Track state for each part of the message
      let partIndex = 0;
      let charIndex = 0;

      // Array to store the content being built up
      const renderedContent: React.ReactNode[] = [];

      // Current text buffer for character-by-character typing
      let textBuffer = '';

      // Define types for our React elements
      type ReactFragmentProps = { children: string };

      // Process next part of the message
      function processNextPart() {
        if (partIndex >= linkedParts.length) {
          // We've processed all parts
          const bubbleElement = document.getElementById(`caMessageBubble-${id}`) as HTMLDivElement;
          if (bubbleElement) {
            setBubbleWidth(bubbleElement.offsetWidth);
          }
          setIsTypingCompleted(true);
          return;
        }

        const currentPart = linkedParts[partIndex];

        // If this is a link, add it immediately (no typing effect)
        if (React.isValidElement(currentPart) && (currentPart.type === Link || currentPart.type === Button)) {
          // Add any pending text buffer first
          if (textBuffer) {
            renderedContent.push(textBuffer);
            textBuffer = '';
          }

          // Add the link directly
          renderedContent.push(currentPart);
          setFormattedMessageContent([...renderedContent]);

          // Scroll to bottom after adding a link
          scrollToBottom?.();

          // Move to next part
          partIndex++;
          setTimeout(processNextPart, speed * 2);
        } else if (React.isValidElement(currentPart) && currentPart.type === React.Fragment) {
          // For text fragments, type character by character
          const fragmentProps = currentPart.props as ReactFragmentProps;
          const textContent = fragmentProps.children || '';

          // Type the next character
          function typeNextChar() {
            if (charIndex < textContent.length) {
              textBuffer += textContent[charIndex];

              // Update displayed content
              setFormattedMessageContent([...renderedContent, textBuffer]);

              // Scroll to bottom as typing happens
              scrollToBottom?.();

              charIndex++;
              setTimeout(typeNextChar, speed);
            } else {
              // Finished typing this text fragment
              if (textBuffer) {
                renderedContent.push(textBuffer);
                textBuffer = '';
              }

              // Move to next part
              charIndex = 0;
              partIndex++;
              setTimeout(processNextPart, speed);
            }
          }

          typeNextChar();
        } else if (Array.isArray(currentPart)) {
          // Handle arrays of elements (like bold text)
          // Add any pending text buffer first
          if (textBuffer) {
            renderedContent.push(textBuffer);
            textBuffer = '';
          }

          // Add each part with typing effect for text fragments
          function processArrayParts(arrayIndex: number) {
            if (arrayIndex >= (currentPart as ReactNode[])?.length) {
              // Move to next main part
              partIndex++;
              setTimeout(processNextPart, speed);
              return;
            }

            const arrayElement = currentPart?.[arrayIndex];

            if (React.isValidElement(arrayElement) && typeof arrayElement.type === 'string' && arrayElement.type === 'b') {
              // Add bold elements directly (no typing effect)
              renderedContent.push(arrayElement);
              setFormattedMessageContent([...renderedContent]);
              scrollToBottom?.();

              // Move to next array element
              arrayIndex++;
              setTimeout(() => processArrayParts(arrayIndex), speed);
            } else if (React.isValidElement(arrayElement) && arrayElement.type === React.Fragment) {
              // Process text fragments character by character
              const fragmentProps = arrayElement.props as ReactFragmentProps;
              const textContent = fragmentProps.children || '';
              let fragmentCharIndex = 0;

              function typeArrayFragmentChar() {
                if (fragmentCharIndex < textContent.length) {
                  textBuffer += textContent[fragmentCharIndex];
                  setFormattedMessageContent([...renderedContent, textBuffer]);
                  scrollToBottom?.();

                  fragmentCharIndex++;
                  setTimeout(typeArrayFragmentChar, speed);
                } else {
                  // Finished typing this fragment
                  if (textBuffer) {
                    renderedContent.push(textBuffer);
                    textBuffer = '';
                  }

                  // Move to next array element
                  arrayIndex++;
                  setTimeout(() => processArrayParts(arrayIndex), speed);
                }
              }

              typeArrayFragmentChar();
            } else {
              // Add other elements directly
              renderedContent.push(arrayElement);
              setFormattedMessageContent([...renderedContent]);
              scrollToBottom?.();

              // Move to next array element
              arrayIndex++;
              setTimeout(() => processArrayParts(arrayIndex), speed);
            }
          }

          processArrayParts(0);
        } else {
          // Handle any other element types
          // Just add it directly
          renderedContent.push(currentPart);
          setFormattedMessageContent([...renderedContent]);

          // Scroll to bottom after adding any element
          scrollToBottom?.();

          partIndex++;
          setTimeout(processNextPart, speed);
        }
      }

      // Start processing the message
      setFormattedMessageContent([]);
      processNextPart();
    },
    [id],
  );

  const linkify = useCallback((text: string) => {
    const urlRegex = /(\b(?:https?:\/\/|www\.)[^\s<>"]+[^\s<>"'.,!?;])/gi;
    const boldRegex = /\*\*([^*]+)\*\*/g;
    const extRegex = /<ext>([^<]*)<\/ext>/g;

    // First handle <ext> tags - split by the regex to identify positions
    const extParts = text.split(extRegex);
    const processedParts: (string | React.ReactNode)[] = [];

    for (let i = 0; i < extParts.length; i++) {
      if (i % 2 === 0) {
        // This is regular text (not inside <ext> tags)
        processedParts.push(extParts[i]);
      } else {
        // This was inside <ext> tags - replace with SignInComponent
        // You'll need to import SignInComponent at the top of the file
        processedParts.push(<SignInComponent key={`signin-${i}`} />);
      }
    }

    // Now process URLs and bold text for each text part
    return processedParts
      .map((part, partIndex) => {
        if (typeof part !== 'string') {
          // This is already a React component (SignInComponent)
          return part;
        }

        // Handle URLs in this text part
        const urlParts = part.split(urlRegex);

        return urlParts.map((urlPart, index) => {
          if (urlPart.match(urlRegex)) {
            const href = urlPart.startsWith('http') ? urlPart : 'https://' + urlPart;
            const urlObject = urlMapping?.find((item) => item.url === href);
            let urlLabel = urlObject?.label || urlPart;
            if (urlObject?.openInSameTab) urlLabel = urlLabel?.replace(/\b\w/g, (c) => c.toUpperCase());
            const isBold = urlObject?.isBold || false;
            return (
              <Link
                key={`${partIndex}-${index}`}
                to={href}
                withIcon
                target={urlObject?.openInSameTab ? '_self' : '_blank'}
                // skin={'cta'} //TODO
                style={{ wordBreak: 'break-word' }}
                fontSkin={isBold ? 'body-standard-bold' : 'body-standard'}
              >
                {urlLabel}
                {!urlObject?.openInSameTab ? <Icon iconType="externalLink" size="20p" /> : null}
              </Link>
            );
          } else {
            // Handle bold text in non-URL parts
            if (boldRegex.test(urlPart)) {
              // Reset regex state
              boldRegex.lastIndex = 0;

              const subParts: React.ReactNode[] = [];
              let lastIndex = 0;
              let match;

              while ((match = boldRegex.exec(urlPart)) !== null) {
                // Add text before the match
                if (match.index > lastIndex) {
                  subParts.push(<React.Fragment key={`${partIndex}-${index}-${lastIndex}`}>{urlPart.substring(lastIndex, match.index)}</React.Fragment>);
                }

                // Add the bold text - match[1] now contains the text between ** **
                subParts.push(React.createElement('b', { key: `${partIndex}-${index}-bold-${match.index}` }, match[1]));

                lastIndex = match.index + match[0].length;
              }

              // Add any remaining text
              if (lastIndex < urlPart.length) {
                subParts.push(<React.Fragment key={`${partIndex}-${index}-last`}>{urlPart.substring(lastIndex)}</React.Fragment>);
              }

              return subParts;
            }

            return <React.Fragment key={`${partIndex}-${index}`}>{urlPart}</React.Fragment>;
          }
        });
      })
      .flat();
  }, []);
  useEffect(() => {
    let timeout: number | undefined;
    /** Typing effect only for new messages and only if window is open */
    if (messageType === 'chatbot' && typeof messageContent === 'string' && !isMessageRead && !disableTypingEffect && chatWindow === 'maximized') {
      // Delay typing to match bubble grow animation
      timeout = setTimeout(() => {
        typeMessageWithLinks(messageContent, 10);
      }, 800);
    } else {
      setIsTypingCompleted(true);

      if (typeof messageContent === 'string') {
        setFormattedMessageContent(linkify(messageContent));
      } else {
        setFormattedMessageContent(messageContent);
      }
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [messageContent, messageType]);

  /** Fetch the avatar letter based on the agent name */
  const getAvatarContent = useCallback(() => {
    const agentFirstChar = agentName?.charAt(0).toUpperCase();
    if (messageType === 'agent' && agentFirstChar && agentFirstChar !== '') {
      return agentFirstChar;
    }
    return footer?.agentName?.[0].toUpperCase() || 'S';
  }, [footer, agentName]);

  const getIcon = useCallback(() => {
    if (messageType === 'chatbot' && (isLastMessage || !isTypingCompleted)) {
      return <Icon iconType="aiSupport" className="ca-message-bubble-avatar" marginRight={2} />;
    } else if (messageType === 'agent' && isLastMessage) {
      return (
        <Box className="ca-message-bubble-avatar-agent" fontSize="small" fontWeight="bold" marginRight={2}>
          {getAvatarContent()}
        </Box>
      );
    } else {
      return <Box style={{ width: '20px', height: '20px' }} marginRight={2} />;
    }
  }, [messageType, isLastMessage, isTypingCompleted]);

  function getAttachmentPreview(attachment) {
    const { mimeType } = attachment;

    type AllowedIconType = 'fileGeneric' | 'fileJpg' | 'filePng' | 'filePdf';
    let fileIconType: AllowedIconType = 'fileGeneric';

    if (mimeType === 'image/jpeg' || mimeType === 'image/jpg') {
      fileIconType = 'fileJpg';
    } else if (mimeType === 'image/png') {
      fileIconType = 'filePng';
    } else if (mimeType === 'application/pdf') {
      fileIconType = 'filePdf';
    }

    return <Icon iconType={fileIconType} size="20p" style={{ background: 'black', margin: '14px' }} />;
  }

  const handleDownload = () => {
    const fileUrl = attachments && attachments[0]?.url;
    const link = document.createElement('a');
    link.href = fileUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <FlexBox paddingBottom={messageContent == '' ? '0' : isLastMessage ? '6' : '2'} compactMode flexDirection="column">
      {(messageContent !== '' || attachments) && (
        <FlexBox>
          {/** Avatar to be displayed only for chatbot/agent message and only if single message is present */ getIcon()}
          <Card
            className={`
              ca-message-bubble-base
              ca-message-bubble-${messageType}
              ${isLastMessage ? `ca-message-bubble-${messageType}-lastMessage` : ''}
              ${typeof messageContent !== 'string' && messageType !== 'agent' ? 'ca-message-bubble-element' : ''}
              ${!isMessageRead ? `ca-message-bubble-${messageType}-animate` : ''}`}
            fullBleed
            style={!isLastMessage ? { borderRadius: tokens.SwanSemBorderRadiusStrong } : {}}
            darkMode={messageType === 'user'}
            compactMode
            id={`caMessageBubble-${id}`}
            mb={attachments ? '4' : '0'}
          >
            <Box className={`ca-message-bubble-message-content-base ca-message-bubble-message-content-${messageType}`} id={`caMessageContent-${id}`}>
              {formatType && formatType == 'Attachments' && attachments && (
                <Card bordered style={{ backgroundColor: 'white' }} display={'flex'} p="4">
                  <Box width="48px" height="48px" style={{ borderRadius: '12px' }}>
                    {getAttachmentPreview(attachments[0])}
                  </Box>
                  <Box ml="4">
                    <FlexBox style={{ height: '100%' }} justifyContent="center" alignItems="center">
                      <Typography fontSkin="body-standard-bold" style={{ color: 'black' }}>
                        {attachments[0].name}
                      </Typography>
                    </FlexBox>

                    {/* <Box
                    style={{
                      color: 'var(--sem-color-text-subtle, #6C6C6C)',
                      fontFamily: 'var(--base-font-family-primary, Graphik)',
                      fontSize: 'var(--sem-font-size-small, 12px)',
                      fontStyle: 'normal',
                      fontWeight: 400,
                      lineHeight: '150%',
                      letterSpacing: '0.06px',
                    }}
                  >
                    3.56 MB
                  </Box> */}
                  </Box>
                  {messageType === 'agent' ? (
                    <Icon onClick={() => handleDownload()} iconType={'download'} size="20p" style={{ background: 'black', margin: '14px' }} />
                  ) : (
                    <></>
                  )}
                </Card>
              )}
              {formattedMessageContent}
            </Box>
            {footer?.isVisible && isLastMessage && (
              <Box className={`ca-message-bubble-footer-${messageType}`} paddingTop="4">
                {formattedAgentName}
                {formattedTime}
              </Box>
            )}
          </Card>
        </FlexBox>
      )}
      {isTypingCompleted && showFeedback && messageType === 'chatbot' && (
        <MessageFeedback
          bubbleWidth={bubbleWidth}
          messageId={id.split('__')[0]} // Removing the msg index to match with request id
        />
      )}
      {isTypingCompleted && (
        <Box style={{ paddingLeft: '24px', maxWidth: '90%' }} className="ca-message-additional-component">
          {additionalComponents}
        </Box>
      )}
    </FlexBox>
  );
};

export default MessageComponentBase;
